import React from "react";
import { motion } from "framer-motion";
import { SmileMark } from "../brand/Brand";
import { scrollToId } from "../../hooks/useLenis";

const headlineLines = [
  "Le premier réseau",
  "moderne de commerces",
  "de proximité d'Afrique.",
];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-diallo-red text-white grain"
      style={{ background: "linear-gradient(180deg, #ED1C24 0%, #ED1C24 55%, #C9151C 100%)" }}
    >
      {/* Parallax smile watermark */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, rotate: -8 }}
        animate={{ opacity: 0.12, scale: 1, rotate: 0 }}
        transition={{ duration: 1.4, ease: "easeOut", delay: 0.2 }}
        className="pointer-events-none absolute -right-24 -bottom-24 md:-right-16 md:-bottom-16"
      >
        <SmileMark color="#FFFFFF" size={620} strokeWidth={5} />
      </motion.div>
      <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-white/5 blur-2xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 min-h-[100svh] flex flex-col justify-center pt-24 pb-16">
        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="font-display font-extrabold tracking-tight text-5xl md:text-6xl lg:text-[5.4rem] leading-[1.12] max-w-[16ch]"
        >
          {headlineLines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={lineChild} className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-7 max-w-[46ch] text-lg md:text-xl text-white/90 leading-relaxed"
        >
          Nous transformons les boutiques de quartier en franchises Diallo. Même
          propriétaire. Même quartier. Une toute nouvelle expérience.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.25 }}
          className="mt-5 font-display italic text-xl md:text-2xl text-white"
        >
          « Le quartier me connaît. »
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => scrollToId("devenir-diallo")}
            data-testid="hero-cta-primary"
            className="rounded-xl bg-white px-8 py-4 font-semibold text-diallo-red shadow-lg shadow-black/10 transition-colors hover:bg-diallo-sand"
          >
            Devenir partenaire
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => scrollToId("le-concept")}
            data-testid="hero-cta-secondary"
            className="rounded-xl border-2 border-white px-8 py-4 font-semibold text-white transition-colors hover:bg-white hover:text-diallo-red"
          >
            Découvrir le concept
          </motion.button>
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="h-10 w-6 rounded-full border-2 border-white/60 flex items-start justify-center p-1"
        >
          <span className="h-2 w-1 rounded-full bg-white/80" />
        </motion.div>
      </motion.div>
    </section>
  );
}
